import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  ElementRef,
  AfterContentChecked,
  inject,
} from '@angular/core';
import {
  FormlyFieldConfig,
  FieldArrayType,
  FormlyModule,
} from '@ngx-formly/core';
import {
  GridComponent,
  RowArgs,
  GridModule,
} from '@progress/kendo-angular-grid';
import { InvestmentWizardService } from '../../../investment-wizard/services/investment-wizard-service.service';
import {
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { SessionStorageService } from '../../../services/session-storage.service';
import { FormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { WarnDialogComponent } from '../../../warn-dialog/warn-dialog.component';
import { DocumentReaderService } from '../../../services/document-reader.service';
import { Subject } from 'rxjs';
import { UnifiedFlowService } from '../../../unified-flow/unified.service';
import { FormBuilderService } from '../../form-builder.service';
import { Location } from '@angular/common';
import { ApiResponse } from '../../../models/general-models';
import { Template } from '../../../models/account-models';
import { FiveTwoNine } from '../../../models/iw-models';
import { NgxLoadingModule } from 'ngx-loading';
import { MatSelectModule } from '@angular/material/select';
import { FundSearchComponent } from '../../../shared-components/fund-search/fund-search.component';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-form-grid-expand',
  templateUrl: './form-gridExpand.component.html',
  styleUrls: ['form-gridExpand.component.scss'],
  imports: [
    NgxLoadingModule,
    MatSelectModule,
    FundSearchComponent,
    MatIconModule,
    GridModule,
    MatCheckboxModule,
    FormlyModule,
    ReactiveFormsModule,
    FormsModule,
    MatTooltipModule,
    MatButtonModule,
  ],
})
export class FormlyGridExpandComponent
  extends FieldArrayType
  implements OnInit, AfterContentChecked
{
  private iws = inject(InvestmentWizardService);
  private ss = inject(SessionStorageService);
  private changeDetector = inject(ChangeDetectorRef);
  private snacky = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private docReadSvc = inject(DocumentReaderService);
  private unfService = inject(UnifiedFlowService);
  private formBuilderService = inject(FormBuilderService);
  private location = inject(Location);

  @ViewChild('gridExp', { static: true }) public grid: GridComponent;
  @ViewChild('csvUpload') csvUploadVariable!: ElementRef;

  columns;
  subFields;
  gridData;
  loading;
  noRecords = 'Please Add an Entry';
  searchOptions = null;
  showTemplates = false;
  placeholder = 'Select a template to apply';
  label = 'Select a template to apply';
  templates;
  firmTemplates;
  program;
  buttonOption;
  hideDelete = false;
  rights = this.ss.get('rights');
  totalInvestment = 0;
  assetAmount = '$0';
  mode = 'amount';
  totalPercent = 0;
  showTotals = false;
  totalsCondit;
  idVar;
  parentIdVar;
  needsRowId = false;
  rowIdPrefix;
  rowId;
  investmentProgramLabel = '';
  fiveTwoNineSearch = false;
  plans529: FiveTwoNine[] = [];
  selected529;
  uploadingFile = false;
  unsubscribe = new Subject();
  canSearchInvestments: boolean;
  canUseFundLookupPopulateBtn = false;
  fundLookupLoading = false;

  public expandedDetailKeys = [];
  public readonly EXPAND_FIELDS_MAPPING = [
    'CurrentPosition_FundID',
    'CPRolloverIRADirFundDetails_FundID',
    'CPRolloverBroFundDetails_FundID',
    'CPRolloverDirFundDetails_FundID',
    'CPRolloverIRABroFundDetails_FundID',
    'CurrentAltInvestmentHoldings_AltInvestmentSponsors',
  ];
  public readonly SELL_FIELDS_MAPPING = [
    'CurrentPosition_Sell',
    'CPRolloverIRADirFundDetails_Sell',
    'CPRolloverBroFundDetails_Sell',
    'CPRolloverDirFundDetails_Sell',
    'CPRolloverIRABroFundDetails_Sell',
  ];
  public readonly HOLD_SELL_FIELDS_MAPPING = [
    'CurrentPosition_HoldOrSell',
    'CPRolloverIRADirFundDetails_HoldOrSell',
    'CPRolloverBroFundDetails_HoldOrSell',
    'CPRolloverDirFundDetails_HoldOrSell',
    'CPRolloverIRABroFundDetails_HoldOrSell',
  ];

  public fileName: string;
  public file: File;

  expandDetailsBy = ({ dataItem, index }: RowArgs) => {
    if (!dataItem) {
      return null;
    }

    for (const field of this.SELL_FIELDS_MAPPING) {
      if (dataItem[field]) {
        return `${index}`;
      }
    }
    for (const field of this.HOLD_SELL_FIELDS_MAPPING) {
      if (dataItem[field] === 'Sell') {
        return `${index}`;
      }
    }
    if (
      dataItem['CurrentAltInvestmentHoldings_AltInvestmentSponsors'] === 'Other'
    ) {
      return `${index}`;
    }

    return null;
  };
  externalButtonOption: string = '';

  ngOnInit() {
    this.onPopulate(this.field);

    const parentVars = this.field.parent.fieldGroup?.map(x => x.key);
    this.parentIdVar = parentVars?.find(
      (x: string) => x.includes('SourceBridgeID') || x.includes('TSPSourceID')
    ) as string;

    const vars = this.field.fieldArray['fieldGroup']?.map(x => x.key);
    this.idVar = vars?.find(
      (x: string) => x.includes('SourceBridgeID') || x.includes('TSPSourceID')
    ) as string;

    // TODO: this should be a temporary fix for the rowId being set when this field is used in the tabbedRepeat
    setTimeout(() => {
      if (this.idVar) {
        this.needsRowId = true;
        this.rowIdPrefix = this.idVar.split('_')[0];
        this.rowId = this.field.parent.model[this.parentIdVar];
      }
    });

    this.buttonOption = this.field.fieldArray['props'].btnText;
    this.showTemplates =
      this.field.fieldArray['props'].showTemplates &&
      this.rights.includes('IWTemplates');
    this.searchOptions = this.field.fieldArray['props'].search;
    this.canUseFundLookupPopulateBtn =
      this.field.parent.parent.parent.model.UnitExternalBridge_Include;
    if (this.searchOptions) {
      this.checkCanSearchInvestments();
      this.field.parent.parent.parent.form.valueChanges
        .pipe(tap())
        .subscribe(() => {
          this.changeDetector.markForCheck();
        });
    }
    this.columns = this.field.fieldArray['fieldGroup']?.filter(el => {
      if (!el.props.subField) {
        return true;
      }
    });
    this.subFields = this.field.fieldArray['fieldGroup']?.filter(el => {
      if (el.props.subField) {
        return true;
      }
    });

    this.columns = this.columns?.map(el => {
      const fld = {
        label:
          (Object.prototype.hasOwnProperty.call(
            el.expressions,
            'props.label'
          ) &&
            el.expressions['props.label']?.()) ||
          el.props.label ||
          el.props.placeholder,
        key: el.key,
        help: el.props.help,
        visuallyHidden: el.className.includes('visual-hidden'),
        hide: el.expressions.hide?.() ?? false,
      };
      return fld;
    });

    // this.getColumnData();

    if (this.model) {
      this.checkExpandedRows();
    }

    this.totalsCondit = this.field.fieldArray['props']['showTotals'];
    this.showTotals = this.formBuilderService.evalConditional(
      true,
      this.totalsCondit
    );

    this.hideDelete = this.field.fieldArray['props'].hideDelete;

    this.noRecords = this.field.props.placeholder
      ? this.field.props.placeholder
      : this.noRecords;

    if (this.showTemplates) {
      this.getTemplates(
        this.field.parent.model.ValidateProduct.InvestmentValidation_Program
      );
    }

    if (this.searchOptions) {
      if (this.searchOptions.fundtype.includes('529')) {
        this.fiveTwoNineSearch = true;
        this.get529();
      }

      this.program =
        this.field.parent.model.ValidateProduct?.InvestmentValidation_Program;
      if (
        this.field.parent.model.UNITPushToUI_NonFilteredPrograms &&
        this.field.parent.model.UNITPushToUI_NonFilteredPrograms.includes(
          this.field.parent.model.ValidateProduct.InvestmentValidation_Program
        )
      ) {
        this.program = null;
      }

      this.field.form.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(z => {
          this.program = z.ValidateProduct?.InvestmentValidation_Program;
          if (this.program) {
            this.getTemplates(z.ValidateProduct.InvestmentValidation_Program);
          }

          if (
            this.field.parent.model.UNITPushToUI_NonFilteredPrograms &&
            this.field.parent.model.UNITPushToUI_NonFilteredPrograms.includes(
              this.field.parent.model.ValidateProduct
                .InvestmentValidation_Program
            )
          ) {
            this.program = null;
          }
          this.assetAmount = z.ValidateProduct?.InvestmentWizard_AssetAmount;
          this.totalInvestment = 0;
          this.totalPercent = 0;

          if (z.SelectedFunds) {
            z.SelectedFunds.forEach(fnd => {
              if (fnd.SelectedFunds_Amount) {
                this.totalInvestment += this.makeNumber(
                  fnd.SelectedFunds_Amount
                );
              } else if (fnd.SelectedFunds_Percent) {
                this.totalPercent =
                  this.totalPercent +
                  +fnd.SelectedFunds_Percent.replace('%', '') / 100;
              }
            });
          }

          if (z.InvestmentValidation_PickPctOrAmt === 'P') {
            this.mode = 'percent';
          } else {
            this.mode = 'amount';
          }

          this.showTotals = this.formBuilderService.evalConditional(
            true,
            this.totalsCondit
          );
        });
    }
    this.investmentProgramLabel =
      this.field.parent.model.ProgramDefinition_DisplayName &&
      this.field.parent.model.ProgramDefinition_ProgramID
        ? this.field.parent.model.ProgramDefinition_DisplayName[
            this.field.parent.model.ProgramDefinition_ProgramID.indexOf(
              this.field.parent.model.ValidateProduct
                .InvestmentValidation_Program
            )
          ]
        : '';
    this.externalButtonOption =
      this.field?.fieldArray?.['props']?.externalBtnText || '';

    if (this.docReadSvc.currentDataDetail) {
      this.applyParsedDocValues(this.docReadSvc.currentDataDetail);
      this.unfService.setLoading(false);
    }

    this.docReadSvc.parsedDocDataDetail$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: docDataDetail => {
          this.applyParsedDocValues(docDataDetail);
          this.unfService.setLoading(false);
        },
        error: error => alert(error),
      });
    this.field.options.build(this.field);
  }

  ngAfterContentChecked() {
    // this.getColumnData();
    if (this.searchOptions) {
      this.checkCanSearchInvestments();
    }
  }

  getColumnData() {
    this.columns = this.field.fieldArray['fieldGroup']?.filter(el => {
      if (!el.props.subField) {
        return true;
      }
    });
    this.subFields = this.field.fieldArray['fieldGroup']?.filter(el => {
      if (el.props.subField) {
        return true;
      }
    });

    this.columns = this.columns?.map(el => {
      const fld = {
        label:
          (Object.prototype.hasOwnProperty.call(
            el.expressions,
            'props.label'
          ) &&
            el.expressions['props.label']?.()) ||
          el.props.label ||
          el.props.placeholder,
        key: el.key,
        help: el.props.help,
        visuallyHidden: el.className.includes('visual-hidden'),
        hide: el.expressions.hide?.() ?? false,
      };
      return fld;
    });
  }

  checkCanSearchInvestments() {
    this.canSearchInvestments =
      !this.searchOptions.conditional ||
      this.formBuilderService.evalConditional(
        null,
        this.searchOptions.conditional,
        null,
        this.field
      );
  }

  checkExpandedRows() {
    this.model.map((fund, index) => {
      this.EXPAND_FIELDS_MAPPING.forEach(field => {
        if (
          field === 'CurrentPosition_FundID' &&
          (fund.CurrentPosition_Sell ||
            fund.CurrentPosition_HoldOrSell === 'Sell')
        ) {
          const fundList = [...this.expandedDetailKeys];
          if (!fundList.includes(`${index}`)) fundList.push(`${index}`);
          this.expandedDetailKeys = fundList;
        }

        if (
          field === 'CPRolloverIRADirFundDetails_FundID' &&
          (fund.CPRolloverIRADirFundDetails_Sell ||
            fund.CPRolloverIRADirFundDetails_HoldOrSell === 'Sell')
        ) {
          const fundList = [...this.expandedDetailKeys];
          if (!fundList.includes(`${index}`)) fundList.push(`${index}`);
          this.expandedDetailKeys = fundList;
        }

        if (
          field === 'CPRolloverBroFundDetails_FundID' &&
          (fund.CPRolloverBroFundDetails_Sell ||
            fund.CPRolloverBroFundDetails_HoldOrSell === 'Sell')
        ) {
          const fundList = [...this.expandedDetailKeys];
          if (!fundList.includes(`${index}`)) fundList.push(`${index}`);
          this.expandedDetailKeys = fundList;
        }

        if (
          field === 'CPRolloverDirFundDetails_FundID' &&
          (fund.CPRolloverDirFundDetails_Sell ||
            fund.CPRolloverDirFundDetails_HoldOrSell === 'Sell')
        ) {
          const fundList = [...this.expandedDetailKeys];
          if (!fundList.includes(`${index}`)) fundList.push(`${index}`);
          this.expandedDetailKeys = fundList;
        }

        if (
          field === 'CPRolloverIRABroFundDetails_FundID' &&
          (fund.CPRolloverIRABroFundDetails_Sell ||
            fund.CPRolloverIRABroFundDetails_HoldOrSell === 'Sell')
        ) {
          const fundList = [...this.expandedDetailKeys];
          if (!fundList.includes(`${index}`)) fundList.push(`${index}`);
          this.expandedDetailKeys = fundList;
        }

        if (
          field === 'CurrentAltInvestmentHoldings_AltInvestmentSponsors' &&
          fund.CurrentAltInvestmentHoldings_AltInvestmentSponsors === 'Other' &&
          !this.field.fieldGroup[index].fieldGroup.find(
            fieldItem => fieldItem.key === this.subFields[0].key
          ).hide
        ) {
          const fundList = [...this.expandedDetailKeys];
          fundList.push(
            fund.CurrentAltInvestmentHoldings_AltInvestmentSponsors
          );
          this.expandedDetailKeys = fundList;
        }
      });
    });
  }

  add(fund?, amount?, percent?) {
    super.add();
    const row = this.model.length - 1;
    if (this.needsRowId) {
      const rowId = this.getField(this.field, { key: this.idVar }, row);
      rowId.formControl.setValue(this.rowId);
      rowId.model[rowId.key as string] = this.rowId;
      this.changeDetector.detectChanges();
    }

    if (fund) {
      const fundId = this.columns?.find(x => x.key.includes('FundID'))?.key;
      const fundIdField = this.getField(this.field, { key: fundId }, row);
      const fundName = this.columns?.find(x => x.key.includes('Name'))?.key;
      const fundNameField = this.getField(this.field, { key: fundName }, row);
      const expenseRatio = this.columns?.find(x =>
        x.key.includes('ExpenseRatio')
      )?.key;
      const expenseRatioField = this.getField(
        this.field,
        { key: expenseRatio },
        row
      );
      const shareClass = this.field?.fieldArray?.['fieldGroup']?.find(
        fieldObj => fieldObj.key.includes('ShareClass')
      )?.key;
      const shareClassField = this.getField(
        this.field,
        { key: shareClass },
        row
      );
      const fundType = this.columns?.find(x => x.key.includes('FundType'))?.key;
      const fundTypeField = this.getField(this.field, { key: fundType }, row);

      if (
        Object.prototype.hasOwnProperty.call(fund, 'FrontLoadSchedule') &&
        fund['FrontLoadSchedule']
      ) {
        const frontLoadSchedule = this.columns?.find(x =>
          x.key.includes('FrontLoadSchedule')
        )?.key;
        const frontLoadScheduleField = this.getField(
          this.field,
          { key: frontLoadSchedule },
          row
        );
        if (frontLoadScheduleField) {
          frontLoadScheduleField.formControl.setValue(fund.FrontLoadSchedule);
          frontLoadScheduleField.model[frontLoadSchedule] =
            fund.FrontLoadSchedule;
          this.changeDetector.detectChanges();
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(fund, 'CDSCSchedule') &&
        fund['CDSCSchedule']
      ) {
        const CDSCSchedule = this.columns?.find(x =>
          x.key.includes('CDSCSchedule')
        )?.key;
        const CDSCScheduleField = this.getField(
          this.field,
          { key: CDSCSchedule },
          row
        );
        if (CDSCScheduleField) {
          CDSCScheduleField.formControl.setValue(fund.CurrentFundCDSC);
          CDSCScheduleField.model[CDSCSchedule] = fund.CurrentFundCDSC;
          this.changeDetector.detectChanges();
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(fund, 'RedemptionFeeSchedule') &&
        fund['RedemptionFeeSchedule']
      ) {
        const redemptionFeeSchedule = this.columns?.find(x =>
          x.key.includes('RedemptionFeeSchedule')
        )?.key;
        const redemptionFeeScheduleField = this.getField(
          this.field,
          { key: redemptionFeeSchedule },
          row
        );
        if (redemptionFeeScheduleField) {
          redemptionFeeScheduleField.formControl.setValue(
            fund.RedemptionFeeSchedule
          );
          redemptionFeeScheduleField.model[redemptionFeeSchedule] =
            fund.RedemptionFeeSchedule;
          this.changeDetector.detectChanges();
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(fund, 'RBCategoryID') &&
        fund['RBCategoryID']
      ) {
        const RBCategoryId = this.columns?.find(x =>
          x.key.includes('RBCategoryId')
        )?.key;
        const RBCategoryIdField = this.getField(
          this.field,
          { key: RBCategoryId },
          row
        );
        if (RBCategoryIdField) {
          RBCategoryIdField.formControl.setValue(fund.RBCategoryID);
          RBCategoryIdField.model[RBCategoryId] = fund.RBCategoryID;
          this.changeDetector.detectChanges();
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(fund, 'UITStandardCD') &&
        fund['UITStandardCD']
      ) {
        const UITStandardCD = this.columns?.find(x =>
          x.key.includes('UITStandardCD')
        )?.key;
        const UITStandardCDField = this.getField(
          this.field,
          { key: UITStandardCD },
          row
        );
        if (UITStandardCDField) {
          UITStandardCDField.formControl.setValue(fund.UITStandardCD);
          UITStandardCDField.model[UITStandardCD] = fund.UITStandardCD;
          this.changeDetector.detectChanges();
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(fund, 'UITStandardDeferred') &&
        fund['UITStandardDeferred']
      ) {
        const UITStandardDeferred = this.columns?.find(x =>
          x.key.includes('UITStandardDeferred')
        )?.key;
        const UITStandardDeferredField = this.getField(
          this.field,
          { key: UITStandardDeferred },
          row
        );
        if (UITStandardDeferredField) {
          UITStandardDeferredField.formControl.setValue(
            fund.UITStandardDeferred
          );
          UITStandardDeferredField.model[UITStandardDeferred] =
            fund.UITStandardDeferred;
          this.changeDetector.detectChanges();
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(
          fund,
          'UITStandardOrganizationCost'
        ) &&
        fund['UITStandardOrganizationCost']
      ) {
        const UITStandardOrganizationCost = this.columns?.find(x =>
          x.key.includes('UITStandardOrganizationCost')
        )?.key;
        const UITStandardOrganizationCostField = this.getField(
          this.field,
          { key: UITStandardOrganizationCost },
          row
        );
        if (UITStandardOrganizationCostField) {
          UITStandardOrganizationCostField.formControl.setValue(
            fund.UITStandardOrganizationCost
          );
          UITStandardOrganizationCostField.model[UITStandardOrganizationCost] =
            fund.UITStandardOrganizationCost;
          this.changeDetector.detectChanges();
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(fund, 'UITTerminationDate') &&
        fund['UITTerminationDate']
      ) {
        const UITTerminationDate = this.columns?.find(x =>
          x.key.includes('UITTerminationDate')
        )?.key;
        const UITTerminationDateField = this.getField(
          this.field,
          { key: UITTerminationDate },
          row
        );
        if (UITTerminationDateField) {
          UITTerminationDateField.formControl.setValue(fund.UITTerminationDate);
          UITTerminationDateField.model[UITTerminationDate] =
            fund.UITTerminationDate;
          this.changeDetector.detectChanges();
        }
      }

      if (fundTypeField) {
        fundTypeField.formControl.setValue(fund.FundType);
        fundTypeField.model[fundType] = fund.FundType;
        this.changeDetector.detectChanges();
      }

      if (fundIdField) {
        fundIdField.formControl.setValue(fund.FundID);
        fundIdField.model[fundId] = fund.FundID;
        this.changeDetector.detectChanges();
      }

      if (fundNameField) {
        fundNameField.formControl.setValue(fund.FundName || fund.Name);
        fundNameField.model[fundName] = fund.FundName || fund.Name;
        this.changeDetector.detectChanges();
      }

      if (expenseRatioField) {
        expenseRatioField.formControl.setValue(fund.ExpenseRatio);
        expenseRatioField.model[expenseRatio] = fund.ExpenseRatio;
        this.changeDetector.detectChanges();
      }

      if (shareClassField) {
        shareClassField.formControl.setValue(fund.ShareClass);
        shareClassField.model[shareClass] = fund.ShareClass;
        this.changeDetector.detectChanges();
      }
    }

    if (amount) {
      const fundAmount = this.columns?.find(x => {
        return x.key.includes('Amount') && !x.key.includes('InitialAmount');
      }).key;
      const fundAmountField = this.getField(
        this.field,
        { key: fundAmount },
        row
      );

      if (fundAmountField) {
        fundAmountField.formControl.setValue(amount);
        fundAmountField.model[fundAmount] = amount;
        this.changeDetector.detectChanges();
      }

      this.totalInvestment += +amount;
    }

    if (percent) {
      const fundPercent = this.columns?.find(x =>
        x.key.includes('Percent')
      ).key;
      const fundPercentField = this.getField(
        this.field,
        { key: fundPercent },
        row
      );

      if (fundPercentField) {
        fundPercentField.formControl.setValue(`${percent}%`);
        this.changeDetector.detectChanges();
      }

      this.totalInvestment +=
        this.makeNumber(
          this.field?.parent.model.ValidateProduct.InvestmentWizard_AssetAmount
        ) *
        (percent / 100);
      this.totalPercent =
        this.makeNumber(
          this.field?.parent.model.ValidateProduct.InvestmentWizard_AssetAmount
        ) / this.totalInvestment;
    }
    this.focusCell(row, 1);

    this.changeDetector.detectChanges();
  }

  remove(idx, ev?) {
    if (ev) {
      ev.preventDefault();
    }
    // Remove the current row's key if it exists
    this.expandedDetailKeys = this.expandedDetailKeys.filter(
      keyItem => keyItem !== String(idx)
    );

    // Adjust keys for rows below the removed row
    this.expandedDetailKeys = this.expandedDetailKeys.map(keyItem => {
      const keyAsNumber = parseInt(keyItem, 10);
      return keyAsNumber > idx ? String(keyAsNumber - 1) : keyItem;
    });

    // Optional: Remove duplicates (if necessary)
    this.expandedDetailKeys = [...new Set(this.expandedDetailKeys)];
    const localModel = [...this.model];
    const deletedFund = localModel[idx];
    const model = this.field.parent?.parent?.parent?.model;
    const key = this.key as string;
    const varSet = this.columns && this.columns[0].key.split('_')[0];
    const fundId = `${varSet}_FundID`;
    const idVar = this.idVar ? this.idVar.split('_')[1] : null;
    const sourceBridge = `${varSet}_${idVar}`;
    const fundIdx = model[key]
      ? model[key].findIndex(
          x =>
            x[fundId] === deletedFund[fundId] && x[sourceBridge] === this.rowId
        )
      : -999;

    if (
      fundIdx > -1 &&
      this.field.fieldArray &&
      this.field.fieldArray['props'].nested
    ) {
      model[key].splice(fundIdx, 1);
    }
    // Manually remove instead of using super.remove() which is removing 2 items
    const formArray = this.formControl as FormArray;

    // Remove from the model array (what the grid displays)
    this.model.splice(idx, 1);

    // Remove from fieldGroup
    this.field.fieldGroup.splice(idx, 1);

    // Remove from form control
    formArray.removeAt(idx);
    formArray.markAsDirty();

    // Update field array keys to prevent duplicate entries
    this.field.fieldGroup.forEach((fieldGroup, index) => {
      fieldGroup.key = index.toString();
    });

    // Trigger formly field check instead of manual change detection
    this.field.options.checkExpressions?.(this.field);
  }

  focusCell(row, cell) {
    if (this.grid) {
      this.grid.focusCell(row, cell);
    }
  }

  getField(
    field: FormlyFieldConfig,
    column,
    rowIndex: number
  ): FormlyFieldConfig {
    const fieldItem = field.fieldGroup[rowIndex]
      ? field.fieldGroup[rowIndex].fieldGroup.find(f => f.key === column.key)
      : { hide: false };
    if (fieldItem) {
      Object.assign(fieldItem, {
        modelOptions: {
          debounce: {
            default: 250,
          },
        },
      });
      if (
        (typeof fieldItem?.key === 'string' &&
          fieldItem?.key?.includes('HoldOrSell')) ||
        (typeof fieldItem?.type === 'string' &&
          (fieldItem?.type?.toLowerCase().includes('currency') ||
            fieldItem?.type?.toLowerCase().includes('percentage')))
      ) {
        fieldItem.modelOptions = { updateOn: 'change' };
      }
    }
    return fieldItem;
  }

  hideColumn(
    field: FormlyFieldConfig,
    column,
    rowIndex: number
  ): FormlyFieldConfig {
    let fieldVisible;
    if (field.formControl instanceof FormArray) {
      fieldVisible = field.fieldArray['fieldGroup']
        ? field.fieldArray['fieldGroup'].find(f => f.key === column.key)
        : false;
    } else {
      fieldVisible = field.fieldGroup[rowIndex]
        ? field.fieldGroup[rowIndex].fieldGroup.find(f => f.key === column.key)
        : false;
    }

    return fieldVisible.expressions.hide?.() ?? null;
  }

  getModel(model, rowIndex): any {
    return model[rowIndex];
  }

  handleFund(ev) {
    let fundInfo = ev.option.value;

    this.iws
      .fundInfo(ev.option.value.FundID)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(extraFundInfo => {
        if (extraFundInfo.results[0]) {
          fundInfo = { ...fundInfo, ...extraFundInfo.results[0] };
        }
        this.add(fundInfo);
      });
  }

  applyTemplate(ev) {
    if (this.field.parent.model.ValidateProduct.InvestmentWizard_AssetAmount) {
      const investment =
        +this.field.parent.model.ValidateProduct.InvestmentWizard_AssetAmount.replace(
          '$',
          ''
        ).replaceAll(',', '');
      const funds = ev.value.funds;

      let fundList = '';
      funds.forEach((x, i) => {
        if (i !== funds.length - 1) {
          fundList += `${x.ticker},`;
        } else {
          fundList += x.ticker;
        }
      });

      const formattedFunds = {};
      let fundInfo;
      const symbolList = [];

      this.iws.fundInfo(fundList).subscribe(x => {
        fundInfo = x.results;
        fundInfo.forEach(el => {
          if (!symbolList.includes(el.Symbol)) {
            const fnd = {};
            const info = funds.find(x => {
              return x.ticker === el.Symbol;
            });

            const perc = info.percent * 100;

            const amount = investment * (perc / 100);
            fnd['FundName'] = el.FundName;
            fnd['FundID'] = info.ticker;
            fnd['ExpenseRatio'] = +el.ExpenseRatio;
            fnd['FundType'] = el.FundType;

            symbolList.push(el.Symbol);

            this.add(fnd, amount.toFixed(2), perc);
          }
        });
      });
    }
  }

  getTemplates(program) {
    this.iws.getTemplates().subscribe(
      (
        x: ApiResponse<{
          UserTemplates: Template[];
          FirmTemplates: Template[];
        }>
      ) => {
        const userTemplates = x.results.UserTemplates;
        const firmTemplates = x.results.FirmTemplates;

        this.templates =
          userTemplates && userTemplates.length > 0
            ? userTemplates.filter(z => z.program === program)
            : [];
        this.firmTemplates =
          firmTemplates && firmTemplates.length > 0
            ? firmTemplates.filter(z => z.program === program)
            : [];
      }
    );
  }

  templateChosen(ev) {}

  get529() {
    this.iws.get529().subscribe(x => {
      this.plans529 = x;
    });
  }

  handle529(ev) {
    if (this.selected529) {
      const planId = this.field.parent.fieldGroup.find(
        x => x.key === 'Selected529PlansArray_PlanID'
      )?.formControl;
      const planName = this.field.parent.fieldGroup.find(
        x => x.key === 'Selected529PlansArray_PlanName'
      )?.formControl;

      if (planId.value !== this.selected529.PlanId) {
        const length = this.model.length;
        for (let index = 0; index < length; index++) {
          this.remove(index);
        }
      }

      if (planId) {
        planId.setValue(this.selected529.PlanId);
      }
      if (planName) {
        planName.setValue(this.selected529.PlanName);
      }
    }
  }

  makeNumber(num) {
    const formatted = num.toString().replace('$', '').replaceAll(',', '');
    return +formatted;
  }

  changeSell(row, field, column, rowIndex) {
    const expandID = `${row}`;
    const idList = [...this.expandedDetailKeys];
    const theField = this.getField(field, column, rowIndex);

    if (this.expandedDetailKeys.includes(expandID)) {
      idList.splice(
        this.expandedDetailKeys.findIndex(key => key === expandID),
        1
      );
      this.expandedDetailKeys = idList;
      this.model[row][column.key] = false;
      theField.formControl.setValue(false);
    } else {
      idList.push(expandID);
      this.expandedDetailKeys = idList;
      this.model[row][column.key] = true;
      theField.formControl.setValue(true);
    }
    this.field.options.checkExpressions?.(this.field);
  }

  sellChecked(row) {
    const expandID = `${row}`;
    if (this.expandedDetailKeys.includes(expandID)) {
      return true;
    } else {
      return false;
    }
  }

  onFileSelected(event) {
    if (
      event?.target?.files?.[0]?.type === 'text/csv' ||
      event?.target?.files?.[0]?.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      this.file = event.target.files[0];

      if (this.file) {
        this.fileName = this.file.name;
      }
    } else {
      this.snacky.open(`Warning! File type not supported.`, 'Close', {
        duration: 4000,
      });
    }
  }

  completeCsvUpload() {
    const formData = new FormData();
    formData.append('Spreadsheet', this.file);
    this.uploadingFile = true;
    this.iws.uploadHoldingsFile(formData).subscribe({
      next: ({ details, data }) => {
        if (details.FileUploadSuccess === true && data) {
          data.map(fundObj => {
            this.add(fundObj, fundObj.Amount);
          });
          this.uploadingFile = false;
          if (
            details.InvestmentsErrorProcess.length <= 0 &&
            details.InvestmentsNotFound.length <= 0 &&
            !details.InvestmentFailureDetails
          ) {
            this.snacky.open(`File uploaded successfully.`, 'Close', {
              duration: 4000,
            });
          } else {
            if (this.field['showButton']) {
              details.InvestmentsNotFound.map(fundObj => {
                this.add(fundObj, fundObj.Amount);
              });
            } else {
              let displayString = `Investment(s) Not Found:`;
              details.InvestmentsNotFound.forEach(inv => {
                displayString += `</br>${inv.FundID}`;
              });
              if (details.InvestmentsErrorProcess.length > 0) {
                displayString = `Process: ${details.InvestmentsErrorProcess[0]} </br> ${displayString}`;
              }
              if (details.InvestmentFailureDetails) {
                displayString = `${details.InvestmentFailureDetails} </br> ${displayString}`;
              }
              this.dialog.open(WarnDialogComponent, {
                panelClass: 'warn-dialog',
                data: {
                  headline: 'Warning',
                  content: displayString,
                  confirm: 'OK',
                },
              });
            }
          }
        }
        this.fileName = null;
        this.file = null;
        this.csvUploadVariable.nativeElement.value = '';
      },
      error: () => {
        this.snacky.open(`An unexpected error occurred.`, 'Close', {
          duration: 4000,
        });
        this.fileName = null;
        this.file = null;
        this.csvUploadVariable.nativeElement.value = '';
      },
    });
  }

  downloadCsvTemplate(ev) {
    ev.preventDefault();
    window.open('assets/IW-Import-Holdings-template.csv', '_blank');
  }

  applyParsedDocValues(docDataDetail) {
    this.unfService.setLoading(false);

    if (
      docDataDetail.pageDetail &&
      docDataDetail.pageDetail.sectionId &&
      (!docDataDetail.pageDetail.tabId ||
        docDataDetail.pageDetail.tabId === this.rowId) &&
      (docDataDetail.pageDetail.sectionId ===
        this.field.parent?.parent?.parent?.props.sectionId ||
        docDataDetail.pageDetail.sectionId ===
          this.field.parent?.props.sectionId)
    ) {
      docDataDetail.data.forEach(data => {
        this.add(data, data.Amount);
      });

      this.docReadSvc.currentDataDetail = null;
      this.unfService.setLoading(false);
      this.changeDetector.detectChanges();
    }
  }

  checkShowGrid() {
    return this.columns?.some(col => !col.hide);
  }

  fundLookup(): void {
    this.fundLookupLoading = true;
    const symbolList: string[] = [];

    this.model.forEach(fieldValue => {
      if (Object.keys(fieldValue).includes(`${this.rowIdPrefix}_FundID`)) {
        symbolList.push(fieldValue[`${this.rowIdPrefix}_FundID`]);
      }
    });
    this.iws.fundInfo(symbolList).subscribe({
      next: res => {
        res.results.forEach(resItem => {
          const rowToChange = this.field.fieldGroup.find(filterItem => {
            const field = filterItem.fieldGroup.find(fieldItem => {
              return fieldItem.key === `${this.rowIdPrefix}_FundID`;
            });
            return field.formControl.value === resItem.Symbol;
          });

          rowToChange.fieldGroup
            .find(col => col.key === `${this.rowIdPrefix}_Name`)
            .formControl.setValue(resItem.FundName);
          rowToChange.fieldGroup
            .find(col => col.key === `${this.rowIdPrefix}_ExpenseRatio`)
            .formControl.setValue(resItem.ExpenseRatio);
          rowToChange.fieldGroup
            .find(col => col.key === `${this.rowIdPrefix}_ShareClass`)
            .formControl.setValue(resItem.ShareClass);
        });
        this.fundLookupLoading = false;
        this.changeDetector.detectChanges();
      },
      error: () => {
        console.error('Network error');
        this.fundLookupLoading = false;
        this.changeDetector.detectChanges();
      },
    });
  }

  onLinkClick() {
    if (this.field.parent?.props?.sectionId === 'CurrentHoldingsSell') {
      const fundIds = this.field.formControl.value.map(
        (rowItem: {
          CurrentPosition_FundType: string;
          CurrentPosition_FundID: string;
          CurrentPosition_Name: string;
          CurrentPosition_ExpenseRatio: string;
          CurrentPosition_InitialPurchaseDate: any;
          CurrentPosition_RecommendByFPPurchase: string;
          CurrentPosition_RecommendByFPLiquidation: string;
          CurrentPosition_ShareClass: string;
        }) => rowItem.CurrentPosition_FundID
      );
      if (fundIds.length) {
        this.ss.set('IWProductShelfFilters', {
          logic: 'and',
          filters: [
            {
              field: 'FundID',
              operator: 'contains',
              value: fundIds,
            },
          ],
        });
      }
    }
    window.open(
      `${this.location.prepareExternalUrl('/iw/product-shelf/')}${encodeURIComponent(
        this.investmentProgramLabel
      )}`,
      '_blank'
    );
  }
}
